<template>
  <div class="ts-root">
    <div class="ts-arch">
      <div class="ts-arch-label">Upper</div>
      <div class="ts-row">
        <button
          v-for="tooth in upperTeeth"
          :key="'u-' + tooth"
          type="button"
          class="ts-tooth"
          :class="{ 'ts-tooth-on': isSelected(tooth) }"
          :aria-pressed="isSelected(tooth) ? 'true' : 'false'"
          :aria-label="'Tooth ' + tooth"
          :disabled="disabled"
          @click="toggle(tooth)"
        >{{ tooth }}</button>
      </div>
    </div>

    <div class="ts-arch">
      <div class="ts-row">
        <button
          v-for="tooth in lowerTeeth"
          :key="'l-' + tooth"
          type="button"
          class="ts-tooth"
          :class="{ 'ts-tooth-on': isSelected(tooth) }"
          :aria-pressed="isSelected(tooth) ? 'true' : 'false'"
          :aria-label="'Tooth ' + tooth"
          :disabled="disabled"
          @click="toggle(tooth)"
        >{{ tooth }}</button>
      </div>
      <div class="ts-arch-label">Lower</div>
    </div>

    <div class="ts-footer">
      <span class="ts-summary" v-if="selected.length === 0">
        No teeth selected — this photo will show on every plan.
      </span>
      <span class="ts-summary" v-else>
        {{ selected.length }} {{ selected.length === 1 ? 'tooth' : 'teeth' }}:
        <strong>{{ selectedLabel }}</strong>
      </span>
      <button
        v-if="selected.length > 0"
        type="button"
        class="ts-clear"
        :disabled="disabled"
        @click="clearAll"
      >Clear</button>
    </div>
  </div>
</template>

<script>
/**
 * Teeth tagger for a patient photo — permanent dentition, 1-32.
 *
 * Laid out as the mouth is, not as a number line: upper arch left-to-right
 * 1→16, lower arch left-to-right 32→17. That puts tooth 1 above tooth 32 and
 * tooth 16 above 17, so a quadrant reads as a vertical block. A naive 1..32
 * grid looks tidier and is much harder to use chairside, because the person
 * tapping it is looking at a mouth.
 *
 * Primary teeth (OD's A-T) are deliberately absent — the tagging feature is
 * permanent-only by product decision, and utils/photoFilter.js drops anything
 * that isn't 1-32 to match.
 */
export default {
  name: 'ToothSelector',
  props: {
    // v-model: array of ints
    value: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
  },
  computed: {
    selected() {
      return (this.value || [])
        .map((t) => parseInt(t, 10))
        .filter((n) => Number.isFinite(n) && n >= 1 && n <= 32);
    },
    upperTeeth() {
      return Array.from({ length: 16 }, (_, i) => i + 1);       // 1 → 16
    },
    lowerTeeth() {
      return Array.from({ length: 16 }, (_, i) => 32 - i);      // 32 → 17
    },
    selectedLabel() {
      return this.selected.slice().sort((a, b) => a - b).map((t) => `#${t}`).join(', ');
    },
  },
  methods: {
    isSelected(tooth) {
      return this.selected.indexOf(tooth) !== -1;
    },
    toggle(tooth) {
      if (this.disabled) return;
      const next = this.isSelected(tooth)
        ? this.selected.filter((t) => t !== tooth)
        : this.selected.concat(tooth);
      this.$emit('input', next.sort((a, b) => a - b));
    },
    clearAll() {
      if (this.disabled) return;
      this.$emit('input', []);
    },
  },
};
</script>

<style lang="scss" scoped>
.ts-root {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ts-arch {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ts-arch-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a94a6;
}

/* Wraps rather than scrolls: on a narrow tablet a hidden half-arch is worse
   than two lines, because nothing signals the rest is off-screen. */
.ts-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ts-tooth {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;          /* finger target — this gets used chairside on a tablet */
  border-radius: 50%;
  border: 1.5px solid #d4dae4;
  background: #fff;
  color: #4a5568;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;

  &:hover:not(:disabled) {
    border-color: #17a2a2;
  }

  &:focus-visible {
    outline: 2px solid #17a2a2;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
}

.ts-tooth-on {
  background: #17a2a2;
  border-color: #17a2a2;
  color: #fff;
}

.ts-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 2px;
}

.ts-summary {
  font-size: 12px;
  color: #6b7280;

  strong {
    color: #2d3748;
    font-weight: 600;
  }
}

.ts-clear {
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: #17a2a2;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: default; }
}
</style>
